// @ts-check

const axiosModule = require('axios');
const axios = axiosModule.default ?? axiosModule;
const rax = require('retry-axios');
const Bluebird = require('bluebird');
const _ = require('lodash');
const memoize = require('memoizee');

const { mergeDashboardTableRowsByDimension, filterAdAccounts, convertToOthersTable } = require('./table');
const { _addRelativeDateRangeInfoIfLegacy } = require('./utils/dateUtils');
const { aggregateTable } = require('./aggregateTable');
const { getMetricsFromWidget } = require('./utils/widgetUtils');
const { getRelatedAnalyticsMetrics } = require('./utils/metricUtils');
const { getCleanedDashboardFilter } = require('./dashboardFilter');
const moment = require('moment');
const { v4 } = require('uuid');

Bluebird.config({
  cancellation: true,
});

const MAX_CONCURRENT_REQUESTS = 100;

/**
 * @typedef {import("./architecture/AdAccountArchitecture")} AdAccountArchitecture
 * @typedef {import("./architecture/AdrielCampaign")} AdrielCampaign
 * @typedef {import("axios").AxiosInstance} Axios
 * @typedef {import("../types").DashboardTable} DashboardTable
 * @typedef {import("../types").DashboardTableRow} DashboardTableRow
 * @typedef {import("../types").DashboardFilter} DashboardFilter
 * @typedef {import("../types").DateFilter} DateFilter
 * @typedef {import("../types").ConnectAdAccount} ConnectAdAccount
 */

const isBrowser = typeof window !== 'undefined';

let workerCount = isBrowser ? Math.floor((navigator?.hardwareConcurrency || 0) / 2) || 5 : 0; // this is the number of cores, if accessible by the browser.
let workerIndex = 0;
/** @type {Worker[]} workerList */
const workerList = new Array(workerCount);

class DashboardClient {
  /**
   *
   * @param {object} p
   * @param {string} p.apiUrl Url of the api, eg: 'https://app.adriel.com/api
   * @param {string} p.accountId (optional) Account Id displayed on the dashboard. If not provided, it will be found in the session. This option is useful for users with multiple workspaces and back officers looking at someone else dashboard.
   * @param {string} p.cookie  (optional) Cookie to be send. Only useful for development, and mobile app.
   * @param {string} p.token  (optional) JWT Header token for mobile app.
   * @param {string} p.currency  (optional) currency
   * @param {boolean} [p.useWorker] Use WebWorker or not. Default true.
   * @param {boolean} [p.isServer] This client is running on the server, so it should call directly the local server.
   * @param {viewKey} [p.viewKey] (optional) Use viewKey to access restricted route
   * @param {boolean} [p.useWidgetTable] (optional) Use WidgetTable route
   */
  constructor({
    apiUrl,
    accountId = null,
    cookie = null,
    token = null,
    currency = null,
    useWorker = false,
    viewKey = null,
    isServer = false,
    useWidgetTable = false,
  }) {
    // Init axios for any FE or BE
    const options = {
      baseURL: apiUrl,
      withCredentials: true,
      headers: {},
      timeout: 95000, // cloudflare timeout https://github.com/axios/axios/issues/1503
    };
    this.isServer = isServer;
    // If dev mode, allow self-signed SSL.
    if (typeof process != 'undefined' && !(typeof navigator != 'undefined' && navigator.product == 'ReactNative')) {
      try {
        const https = require('https');
        options.httpsAgent = new https.Agent({ rejectUnauthorized: false });
      } catch (error) {
        //
      }
    }
    if (!isBrowser && cookie) {
      options.headers.Cookie = cookie;
    }
    if (accountId) {
      options.headers.accountid = accountId;
    }
    if (token) {
      options.headers.Authorization = token;
    }
    if (viewKey) {
      options.headers.viewKey = viewKey;
    }
    /** @type {Axios} */
    this.axios = axios.create(options);
    /** @type {Axios} */
    this.retryAxios = axios.create(options);
    this.retryAxios.defaults.raxConfig = {
      instance: this.retryAxios,
    };
    rax.attach(this.retryAxios);
    this.accountId = accountId;
    this.currency = currency;

    /** @type {ConnectAdAccount[]} */
    this.analyticAccounts = [];
    this.adAccounts = [];
    this.customMetrics = [];
    this.debug = false;
    this.noCache = false;
    this.useBuiltChildQuery = undefined;
    this.mode = 'getTable';
    this.useRowCache = false; // TODO change to true when the cache is working well
    this.useWidgetTable = useWidgetTable;

    if (typeof window !== 'undefined' && window?.location?.href) {
      this.debug = window.location.href.includes('debug');
      this.noCache = window.location.href.includes('debugInvalidateCache') || window.location.href.includes('noCache');
      this.useBuiltChildQuery = window.location.href.includes('useBuiltChildQuery');
      this.useWidgetTable = window.location.href.includes('widgetTable');
      if (window.location.href.includes('mode=query')) {
        this.mode = 'query';
        console.log(`${this.mode} mode activated`);
      }
      if (window.location.href.includes('noRowCache')) {
        this.useRowCache = false;
      }
      if (window.location.href.includes('useRowCache')) {
        this.useRowCache = true;
      }
    }
    if (this.debug) {
      console.log('DEBUG MODE ACTIVATED');
      if (typeof window !== 'undefined') {
        window.dashboardDebug = true;
      }
    }

    // Enable or disable WebWorkers.
    this.useWorker = useWorker;

    this.memoizedCallTable = this.noCache
      ? this.callTable
      : memoize(this.callTable, {
          normalizer: args => JSON.stringify(args),
          maxAge: 10 * 60 * 1000, // 10 minutes
          max: 1000,
          promise: true,
        });
  }

  async callTable({ body, params }) {
    const baseRoute = 'dashboard/table';
    const result = await this.retryAxios.post(`${baseRoute}/${body?.adAccount?.channel || ''}/`, body, {
      params,
      raxConfig: {
        retry: 2,
        retryDelay: 100,
        httpMethodsToRetry: ['POST'],
        statusCodesToRetry: [[524, 524]],
        shouldRetry: err => {
          const cfg = rax.getConfig(err);
          if (!cfg) return false; // cannot guarantee limits without retry metadata
          const { currentRetryAttempt = 0, retry = 0 } = cfg;
          if (currentRetryAttempt >= retry) return false; // ensure max retries is always respected
          const channel = body?.adAccount?.channel;
          return rax.shouldRetryRequest(err) && channel === 'kakao';
        },
      },
    });
    return result;
  }

  async fetchArchitecture({ architectureFilter, level, architecture = null, assetId = null }) {
    // Call REST API.
    this;
    let res = await this.axios.post('/dashboard/connect/architecture', {
      architectureFilter,
      level,
      assetId,
      useCache: false,
    });
    let partialArchi = res.data;
    // Optionally, update the local architecture.
    if (architecture) {
      architecture.registerElementsFromArchitecture({
        partialArchi,
        levels: ['campaign', 'adSet', 'ad'],
        adAccountId: partialArchi.adAccount.channelId,
        channel: partialArchi.adAccount.channel,
      });
    }
    return partialArchi;
  }
  /**
   * Fetch one element directly from adriel connect without any transform and without using the cache.
   * @param {object} p
   */
  async fetchOneRawFreshArchitectureElement({
    channel,
    level,
    channelAdAccountId,
    channelCampaignId,
    channelAdSetId,
    channelAdId,
    channelKeywordId,
    channelId,
    id,
  }) {
    this;
    let architectureFilter = buildArchitectureFilter({
      channel,
      level,
      channelAdAccountId,
      channelCampaignId,
      channelAdSetId,
      channelAdId,
      channelKeywordId,
    });
    if (!channelId && id) {
      const splittedId = id.split('/');
      channelId = splittedId[splittedId.length - 1];
    }
    let partialArchi = await this.fetchArchitecture({ architectureFilter, level, architecture: null, assetId: null });
    if (partialArchi[level + 's']?.length != 1) throw new Error('The wrong ');
    let rawArchitectureElement = partialArchi[level + 's'][0];
    if (rawArchitectureElement.budget && !rawArchitectureElement.budget.currency)
      rawArchitectureElement.budget.currency = partialArchi.adAccount.currency;
    if (rawArchitectureElement.channelId != channelId) {
      throw new Error(
        'The wrong architecture element was fetched - archiId: ' +
          rawArchitectureElement.channelId +
          ' channelId: ' +
          channelId,
      );
    }
    return rawArchitectureElement;
  }

  async updateElement({
    row: {
      channel,
      channelId,
      level,
      channelAdAccountId,
      channelCampaignId,
      channelAdSetId,
      channelAdId,
      channelKeywordId,
    },
    type,
    value,
    table,
  }) {
    this;
    let architectureFilter = buildArchitectureFilter({
      channel,
      level,
      channelAdAccountId,
      channelCampaignId,
      channelAdSetId,
      channelAdId,
      channelKeywordId,
    });
    // Construct actions.
    let action = { channelId, level, type, value };
    if (level == 'adrielCampaign') {
      throw new Error('not implemented');
    } else {
      action = {
        ...action,
        channel,
        adAccountId: channelAdAccountId,
        partialArchitecture: architectureFilter,
      };
    }
    // Call REST API.
    let res = await this.axios.post('dashboard/connect/update', { action });
    // Update the architecture.
    let actionResult = res.data;

    let { patches } = actionResult;
    if (patches && table) {
      walkTable(table, row => {
        if (!['campaign', 'adSet', 'ad', 'keyword'].includes(row?.dimension?.id)) return;
        let patch = patches[row.id];
        if (!patch) return;
        _.merge(row, patch);
      });
      actionResult.updatedTable = table;
    }
    return actionResult;
  }

  reformatElementId(elementId, level) {
    if (['adSet', 'ad', 'keyword'].includes(level)) {
      return elementId;
    }
    return elementId
      .toString()
      .split(':')
      .filter(item => item != level)
      .join(':');
  }

  async updateKPI({ architecture, row, elementId, level, metric, value }) {
    let kpi = {
      elementId: this.reformatElementId(elementId, level),
      level,
      metric,
      kpiValue: value,
    };

    let res = await this.axios.post('dashboard/kpis', kpi);

    if (res.status == 204) {
      if (row) {
        if (!row.kpi) {
          row.kpi = {};
        }
        row.kpi[metric] = value;
      } else if (architecture) {
        architecture.registerKPI(kpi);
      }
    }

    return res;
  }

  async deleteKPI({ architecture, row, elementId, level, metric, value }) {
    let kpi = {
      elementId: this.reformatElementId(elementId, level),
      level,
      metric,
    };

    let res = await this.axios.delete('dashboard/kpis', { data: kpi });

    if (res.status == 204) {
      if (row) {
        delete row.kpi[metric];
      } else if (architecture) {
        architecture.deleteKPI(kpi);
      }
    }

    return res;
  }

  async getCustomBreakdowns() {
    this;
    const response = await this.axios.get('dashboard/customBreakdowns');
    const customBreakdowns = response.data;
    return customBreakdowns;
  }

  //   async createCustomBreakdownLabel({ customBreakdownId, labelName, dashboardFilter }) {
  //       const response = await this.axios.post(
  //           `dashboard/customBreakdowns/${customBreakdownId}/label`,
  //           {
  //               labelName,
  //               dashboardFilter,
  //           }
  //       );
  //       const label = response.data;
  //       return label;
  //   }

  async createCustomBreakdown({ name, labels }) {
    const response = await this.axios.post(`dashboard/customBreakdowns`, {
      name,
      labels,
    });
    const customBreakdown = response.data;
    return customBreakdown;
  }

  async updateCustomBreakdown({ customBreakdownId, name, labels }) {
    const response = await this.axios.put(`dashboard/customBreakdowns/${customBreakdownId}`, { name, labels });
    return response.data;
  }

  async deleteCustomBreakdown(customBreakdownId) {
    const response = await this.axios.delete(`dashboard/customBreakdowns/${customBreakdownId}`);
    return response.data;
  }
  //   async updateCustomBreakdownLabel({ customBreakdownId, labelId, labelName, dashboardFilter }) {
  //       const response = await this.axios.put(
  //           `dashboard/customBreakdowns/${customBreakdownId}/label/${labelId}`,
  //           {
  //               labelName,
  //               dashboardFilter,
  //           }
  //       );
  //       return response.data;
  //   }

  async getCustomMetrics(viewId) {
    const response = await this.axios.get('dashboard/customMetrics2', { params: { viewId } });
    const customMetrics = response.data;

    this.customMetrics = customMetrics;

    return customMetrics;
  }

  async createCustomMetric(data) {
    const response = await this.axios.post(`dashboard/customMetrics2`, data);
    const customMetric = response.data;

    this.customMetrics.push(customMetric);

    return customMetric;
  }

  async updateCustomMetric({
    customMetricId,
    name,
    type,
    details,
    viewId,
    isOnlyView,
    nbOfDecimalDigits,
    description,
    aggregation,
  }) {
    const response = await this.axios.put(`dashboard/customMetrics2/${customMetricId}`, {
      name,
      type,
      details,
      viewId,
      isOnlyView,
      nbOfDecimalDigits,
      description,
      aggregation,
    });

    this.customMetrics.forEach(customMetric => {
      if (customMetric.id === customMetricId) {
        customMetric.name = name;
        customMetric.type = type;
        customMetric.details = details;
        customMetric.nbOfDecimalDigits = nbOfDecimalDigits;
        customMetric.aggregation = aggregation;
      }
    });

    return response.data;
  }

  async deleteCustomMetric(data) {
    const response = await this.axios.delete(`dashboard/customMetrics2/${data.id}`, { data });

    this.customMetrics = this.customMetrics.filter(customMetric => customMetric.id === data.id);

    return response.data;
  }

  async getAdAccounts(params) {
    if (this.isServer) {
      return this.adAccounts;
    }
    let res = await this.axios.get('dashboard/connect/adAccounts', { params });
    const adAccounts = res.data;
    this.analyticAccounts = adAccounts.filter(adAccount => adAccount.is_analytic);
    this.adAccounts = adAccounts.filter(adAccount => !adAccount.is_analytic);

    return res.data;
  }

  async getAdAccountsV2() {
    let res = await this.axios.get('dashboard/dataSource');
    const adAccounts = res.data;
    this.analyticAccounts = adAccounts.filter(adAccount => adAccount.is_analytic);
    this.adAccounts = adAccounts.filter(adAccount => !adAccount.is_analytic);

    return res.data;
  }

  getObjectiveList() {
    return [
      'APP_INSTALLS',
      'BRAND_AWARENESS',
      'BRAND_SEARCH',
      'CONVERSIONS',
      'ENGAGEMENT',
      'EVENT_RESPONSES',
      'JOB_APPLICANT',
      'LEAD_GENERATION',
      'LINK_CLICKS',
      'MESSAGES',
      'NONE',
      'OFFER_CLAIMS',
      'PAGE_LIKES',
      'POST_ENGAGEMENT',
      'POWER_CONTENTS',
      'PRODUCT_CATALOG_SALES',
      'REACH',
      'SHOPPING',
      'STORE_VISITS',
      'Search',
      'VIDEO_VIEWS',
      'WEB_VIEW',
      'sponsoredBrands',
      'sponsoredDisplay',
      'sponsoredProducts',
    ];
  }

  /**
   * Get the data from the overview graph.
   * @param {object} p
   * @param {ConnectAdAccount[]} p.adAccounts ad accounts.
   * @param {DashboardFilter} p.dashboardFilter
   * @param {DateFilter} p.dateFilter
   * @param {function} [p.reRenderCallBack]
   * @param {function} [p.failCallBack]
   * @param {function} [p.doneCallBack]
   * @returns {Promise<{table: DashboardTableRow, adAccountTable: DashboardTable, isSuccess: Boolean}>[]}
   */
  getOverviewGraphs({
    widgetId,
    adAccounts,
    dateFilter,
    dashboardFilter,
    metricFilter,
    customMetrics = [],
    reRenderCallBack,
    failCallBack,
    doneCallBack,
    widget = null,
  }) {
    return this.legacyGetTable({ ...arguments[0], dashboardFilter, dimensionId: 'autoTime', includeTotal: true });
  }

  /**
   * Get the data from the overview graph.
   * @param {object} p
   * @param {ConnectAdAccount[]} p.adAccounts ad accounts.
   * @param {string} p.currency 3-letter currency code. This is the display currency. If it is different than the original ad account currency, price metrics will be converted.
   * @param {DashboardFilter} p.dashboardFilter
   * @param {DateFilter} p.dateFilter
   * @param {"campaign"|"adAccount"} p.dimensionId
   * @param {function} [p.reRenderCallBack]
   * @param {function} [p.failCallBack]
   * @param {function} [p.doneCallBack]
   * @returns {Promise<{table: DashboardTableRow, adAccountTable: DashboardTable, isSuccess: Boolean, adAccount:ConnectAdAcount>[]}
   */
  getMobileAppTable({
    adAccounts,
    dateFilter,
    dashboardFilter,
    metricFilter,
    dimensionId,
    orderBy,
    orderDirection,
    reRenderCallBack,
    failCallBack,
    doneCallBack,
    customMetrics = [],
  }) {
    return this.legacyGetTable({ ...arguments[0], dashboardFilter, includeTotal: true });
  }

  getGraphWithOthers({
    adAccounts,
    dateFilter,
    dashboardFilter,
    metricFilter,
    dimensionId,
    widgetId = null,
    orderBy,
    orderDirection,
    reRenderCallBack = undefined,
    failCallBack = undefined,
    doneCallBack = undefined,
    widget = null,
    childrenDimensionId = 'autoTime',
  }) {
    const newDoneCallBack = async ({ table }) => {
      const DIMENSIONS_NOT_CALL_OTHERS = ['os'];
      if (table?.length >= 10 && !DIMENSIONS_NOT_CALL_OTHERS.includes(dimensionId)) {
        // get adAccount level metrics
        let { table: adAccountTables, analyticAccount: resultAnalyticAccount } = await this.legacyGetTable({
          ...arguments[0],
          reRenderCallBack: null,
          doneCallBack: null,
          dashboardFilter,
          dimensionId: 'adAccount',
          childrenDimensionId: childrenDimensionId || 'autoTime',
        });

        convertToOthersTable({ table: adAccountTables, dimensionId, dimension: table[0].dimension });

        let copiedTables = _.cloneDeep(table);
        convertToOthersTable({
          table: copiedTables,
          prepareForSubtraction: true,
          dimensionId,
          dimension: table[0].dimension,
        });

        let othersTable = [...(adAccountTables || []), ...(copiedTables || [])];
        othersTable = await this.workerMergeDashboardTableRowsByDimension({
          rows: othersTable,
          dimensionId,
          childrenDimensionId: childrenDimensionId || 'autoTime',
        });

        // delete metrics if it's negative after subtraction
        const metricsToHide = new Set();
        othersTable.map(row => {
          row.children.map(child => {
            Object.entries(child.cell || {}).map(([metricName, metricValue]) => {
              if ((typeof metricValue == 'number' && metricValue < 0) || metricsToHide.has(metricName)) {
                delete child.cell[metricName];
                metricsToHide.add(metricName);
              }
            });
          });

          row.children.map(child => {
            if (child.cell) {
              for (let metricToHide of metricsToHide) {
                delete child.cell[metricToHide];
              }
            }
          });
        });

        table = [...table, ...(othersTable || [])];
      }

      if (this.debug) {
        console.log(
          `getGraphWithOthers
        ${widget?.widget_title ? widget?.widget_title : widget?.id}:${widget?.type}:${
            widget?.dimensionId || widget?.dimensionIds?.[0]
          },
        `,
          table,
        );
      }
      doneCallBack({ table });
    };

    return this.legacyGetTable({
      ...arguments[0],
      doneCallBack: newDoneCallBack,
      dashboardFilter,
      childrenDimensionId: childrenDimensionId || 'autoTime',
    });
  }

  getGraph({
    adAccounts,
    dateFilter,
    dashboardFilter,
    metricFilter,
    dimensionId,
    customMetrics = [],
    widgetId = null,
    orderBy,
    orderDirection,
    reRenderCallBack = undefined,
    failCallBack = undefined,
    doneCallBack = undefined,
    widget = null,
    childrenDimensionId = null,
  }) {
    ({ adAccounts, dashboardFilter } = filterAdAccounts({ adAccounts, dashboardFilter, dimensionId }));
    // dashboardFilter = cleanDashboardFilter(dashboardFilter);
    if (widget?.selectedGraphType === 'pie' || widget?.selectedGraphType === 'scatter') {
      return this.legacyGetTable({ ...arguments[0], childrenDimensionId: null });
    } else {
      return this.getGraphWithOthers({ ...arguments[0], adAccounts, dashboardFilter });
    }
  }

  getAccountSets({
    adAccounts,
    useAllAdAccounts = false,
    isCustomTable = false,
    metrics = [],
    noWidgetPassed = false,
    dimensionId = undefined,
  }) {
    let analyticAccounts = adAccounts.filter(adAccount => adAccount.is_analytic);
    let customReportAccounts = adAccounts.filter(adAccount => adAccount.isCustomReportAccount);
    if (isCustomTable) {
      adAccounts = customReportAccounts;
      analyticAccounts = [];
    } else {
      adAccounts = adAccounts.filter(adAccount => !adAccount.is_analytic && !adAccount.isCustomReportAccount);
    }
    let accountSets = [
      ...adAccounts.map(adAccount => ({ adAccount })),
      ...analyticAccounts
        .map(analyticAccount => {
          if (analyticAccount.channel == 'googleAnalytics') {
            const relatedMetrics = getRelatedAnalyticsMetrics({ analyticAccount, metrics });
            if (relatedMetrics.length > 0 || noWidgetPassed) {
              return {
                adAccounts: useAllAdAccounts ? null : adAccounts,
                adAccount: analyticAccount,
                metrics: relatedMetrics,
              };
            }
          } else {
            return { adAccounts: useAllAdAccounts ? null : adAccounts, adAccount: analyticAccount };
          }
        })
        .flat()
        .filter(Boolean),
    ];
    return accountSets;
  }

  async legacyGetTable({
    adAccounts = null,
    dateFilter,
    dashboardFilter,
    metricFilter,
    dimensionId,
    childrenDimensionId,
    widgetId = null,
    reRenderCallBack = null,
    failCallBack = null,
    doneCallBack = null,
    includeTotal = false,
    doCleanDashboardFilter = false,
    accountSets = null,
    forceRefreshCache = false,
    customMetrics = [],
    isCustomTable = false,
    widget = null,
  }) {
    isCustomTable = isCustomTable;
    const currency = this.currency;
    const timestamp = Number(new Date());
    if (this.debug) {
      console.log(`getTable Input ${dimensionId}-${childrenDimensionId}`, arguments[0]);
    }
    _addRelativeDateRangeInfoIfLegacy(dateFilter);

    // Include the total/sum of the table in the result, if requested.
    if (includeTotal && !isCustomTable) {
      // doesnt support custom table yet
      return this.getTableWithTotal(arguments[0]);
    }
    // Filter ad accounts to avoid useless calls.
    ({ adAccounts, dashboardFilter } = filterAdAccounts({ adAccounts, dashboardFilter, dimensionId }));
    if (adAccounts.length === 0) {
      doneCallBack({ table: [] });
      return [];
    }
    if (doCleanDashboardFilter) dashboardFilter = getCleanedDashboardFilter(dashboardFilter);

    // Group ad accounts in sets. This is useful for GA.
    /**
     * TODO: replace "customMetrics: this.customMetrics" with just "customMetrics". customMetrics is already a param to the function!
     * Be careful about FE side. Could be needed there.
     * At least use it BE side!!!
     *
     * Do an IF check: if(isBrowser) { keep as is } else { getMetricsFromWidget({ widget, customMetrics }) }
     *
     * NOTE: Other functions where FE side get data from an BE endpoint through the Dashboar(Query)Client has this logic that sets the this.<paramName>.
     * When used on BE side, check how the logic is for these data points as well, such as getAdAccounts() possibly more!
     */
    const { metrics } = getMetricsFromWidget({ widget, customMetrics: this.customMetrics });
    if (!accountSets?.length) {
      let useAllAdAccounts = !dashboardFilter.adAccountIds && !dashboardFilter.channels;
      if (this.adAccounts?.length > 0 && useAllAdAccounts) {
        useAllAdAccounts = adAccounts.length === this.adAccounts.length;
      } else {
        useAllAdAccounts = false;
      }
      accountSets = this.getAccountSets({
        adAccounts,
        useAllAdAccounts,
        isCustomTable,
        metrics,
        noWidgetPassed: !widget,
        dimensionId,
      });
    }

    // A first request for the cached data.
    let cache = [];
    const baseBody = { dateFilter, dashboardFilter, metricFilter, dimensionId, childrenDimensionId };
    try {
      cache = (
        await this.memoizedCallTable({
          body: { ...baseBody, accountSets, useCache: true },
          params: { c: `${dimensionId + (childrenDimensionId ? `-${childrenDimensionId}` : '')}-cache` },
        })
      ).data;
    } catch (error) {
      failCallBack({ error });
      throw error;
    }
    let table, lastUpdatedAt;
    let adAccountTables = cache.map(c => c?.table);
    let lastUpdatedAts = cache.map(c => c?.lastUpdatedAt);

    const self = this;

    async function callRerender({ adAccount }) {
      let isCacheLoaded = adAccountTables.every(Boolean);
      lastUpdatedAt = isCacheLoaded && moment.min(lastUpdatedAts.filter(Boolean).map(x => moment(x)));
      table = await self.workerMergeDashboardTableRowsByDimension({
        rows: Object.values(adAccountTables).flat().filter(Boolean),
        dimensionId,
        childrenDimensionId,
        dashboardFilter,
        currency,
        customMetrics,
        isCustomTable,
      });

      if (reRenderCallBack) {
        reRenderCallBack({ table, lastUpdatedAt, adAccount, isCacheLoaded });
      }
    }
    let throttledCallRerender = _.throttle(callRerender, 500, { trailing: false });
    // Call the renderer
    await callRerender({ adAccount: null });

    // // Now call for the fresh data.
    const promises = accountSets.map(async ({ adAccount = null, adAccounts = null, metrics }, accountSetInd) => {
      try {
        let lastUpdatedAt = lastUpdatedAts[accountSetInd];
        const doRefreshCache =
          !lastUpdatedAt || this.noCache || forceRefreshCache || moment().diff(lastUpdatedAt, 'hours') >= 1;
        if (!doRefreshCache) return;
        // Request again for refreshed data
        const callTableResult = await this.memoizedCallTable({
          body: { ...baseBody, adAccount, adAccounts, useCache: false, metrics },
          params: { c: `${dimensionId + (childrenDimensionId ? `-${childrenDimensionId}` : '')}-${adAccount.channel}` },
        });
        ({ table: adAccountTables[accountSetInd], lastUpdatedAt: lastUpdatedAts[accountSetInd] } =
          callTableResult.data);
        // Re-Re-render.
        await throttledCallRerender({ adAccount });
      } catch (error) {
        // 'failCallBack' should NOT be called when REFRESHING cache fails
        if (!adAccountTables[accountSetInd] && failCallBack) {
          const isArchitectureSynced = Boolean(adAccount.architectureSyncedAt);
          if (!isArchitectureSynced && adAccount.hasOwnProperty('architectureSyncedAt')) {
            error = { code: 'ARCHITECTURE_NOT_SYNCED', message: 'architecture is not synced' };
            console.error('ARCHITECTURE_NOT_SYNCED', { adAccount, error });
          }
          failCallBack({ adAccount, error });
        } else {
          console.log('Error while refreshing: channel', adAccount?.channel);
        }
      }
    });

    try {
      await Bluebird.all(promises);
    } catch (error) {
      if (failCallBack) failCallBack({ error });
    }
    throttledCallRerender.cancel();
    await callRerender({ adAccount: null });
    if (doneCallBack) {
      doneCallBack({ table });
      if (this.debug) {
        console.log(`getTable Output ${dimensionId}-${childrenDimensionId}`, { table });
      }
    }
    return { table, lastUpdatedAt };
  }

  async getChildrenOfTableRow({
    parentRow,
    dimensionId,
    customMetrics = [],
    widgetId = null,
    reRenderCallBack = null,
    failCallBack = null,
    doneCallBack = null,
    forceRefreshCache = false,
    widget = null,
  }) {
    // Group ad accounts in sets. This is useful for GA.
    /**
     * TODO: replace "customMetrics: this.customMetrics" with just "customMetrics". customMetrics is already a param to the function!
     * Be careful about FE side. Could be needed there.
     * At least use it BE side!!!
     *
     * Do an IF check: if(isBrowser) { keep as is } else { getMetricsFromWidget({ widget, customMetrics }) }
     *
     * NOTE: Other functions where FE side get data from an BE endpoint through the Dashboar(Query)Client has this logic that sets the this.<paramName>.
     * When used on BE side, check how the logic is for these data points as well, such as getAdAccounts() possibly more!
     */
    const { metrics } = getMetricsFromWidget({ widget, customMetrics: this.customMetrics });

    let table = [];
    await Bluebird.all(
      Object.entries(parentRow.childCursors)
        .map(([assetId, cursor]) => {
          let { adAccount, dashboardFilter, dateFilter, metricFilter } = cursor;
          let adAccounts = [adAccount];
          if (this.analyticAccounts?.length > 0 && !adAccount.is_analytic && !adAccount.isCustomReportAccount) {
            adAccounts.push(...this.analyticAccounts);
          }
          const accountSets = this.getAccountSets({
            adAccounts,
            isCustomTable: adAccount.isCustomReportAccount,
            metrics,
            dimensionId,
          });
          return accountSets.map(async accountSet => {
            try {
              var { table: adAccountTable, analyticAccount: resultAnalyticAccount } = await this.legacyGetTable({
                adAccounts,
                dashboardFilter,
                dateFilter,
                metricFilter,
                dimensionId,
                doCleanDashboardFilter: false,
                accountSets: [accountSet],
                forceRefreshCache,
              });
            } catch (error) {
              failCallBack({ adAccount, error });
            }
            if (adAccountTable?.length > 0) {
              table = [...table, ...(adAccountTable || [])];
              table = await this.workerMergeDashboardTableRowsByDimension({ rows: table, dimensionId, customMetrics });
              parentRow.children = table;
              for (let row of parentRow.children) {
                row.parent = { ..._.pick(parentRow, ['id', 'name', 'dimension', 'parent']) };
                row.tableId = parentRow.tableId + '-' + row.rowId;
              }
            }
            if (reRenderCallBack) reRenderCallBack({ table, adAccount });
          });
          //return this.legacyGetTable({adAccounts, dashboardFilter, dateFilter, metricFilter, dimensionId, doCleanDashboardFilter:false, accountSets })
        })
        .flat(),
    );
    doneCallBack({ table });
    return { table };
  }

  getTableWithTotal(params) {
    let totalTable = { cell: {}, children: [] };
    let isTotalCacheLoaded = false,
      isNonTotalCacheLoaded = false;
    const promises = [
      this.legacyGetTable({
        ...params,
        widgetId: params.widgetId + '-all',
        dimensionId: 'all',
        includeTotal: false,
        reRenderCallBack({ table, lastUpdatedAt, isCacheLoaded }) {
          if (table?.length) {
            totalTable = { ...table[0], children: totalTable.children };
          }
          isTotalCacheLoaded = isCacheLoaded;
          params.reRenderCallBack({
            ...params,
            ...arguments,
            table: totalTable,
            lastUpdatedAt,
            isCacheLoaded: isTotalCacheLoaded && isNonTotalCacheLoaded,
          });
        },
        doneCallBack: () => {},
      }),
      this.legacyGetTable({
        ...params,
        widgetId: params.widgetId + '-children',
        includeTotal: false,
        reRenderCallBack({ table, lastUpdatedAt, isCacheLoaded }) {
          if (table?.length) {
            totalTable.children = table;
          }
          isNonTotalCacheLoaded = isCacheLoaded;
          params.reRenderCallBack({
            ...arguments,
            table: totalTable,
            lastUpdatedAt,
            isCacheLoaded: isTotalCacheLoaded && isNonTotalCacheLoaded,
          });
        },
        doneCallBack: () => {},
      }),
    ];
    Promise.all(promises)
      .catch(error => {
        params.failCallBack({ error });
      })
      .then(() => {
        if (this.debug) {
          const widget = params?.widget;
          console.log(
            `getTableWithTotal
          ${widget?.widget_title ? widget?.widget_title : widget?.id}:${widget?.type}:${params?.dimensionId},
          `,
            totalTable,
          );
        }
        params.doneCallBack({ table: totalTable });
      });
    return promises;
  }

  /**
   * Returns an existing worker, or creates a new one if the pool has space for more workers.
   * @param {number} index
   * @returns Worker from the worker pool
   */
  getOrInitWorkerFromPool(index) {
    if (index > workerCount) {
      index = index % workerCount;
    }

    let worker = workerList[index];
    if (!worker) {
      // NOTE: The type is removed by the worker-plugin (added in the frontend project)! 'module' is not well supported by all browser (FF and Safari).
      // Therefore the plugin removes the options and the default is then type: 'classic'.
      // But if you remove the type or set it to 'classic' it will not work in the browser (tested on Linux/Fedora 32 and FireFox)
      // Source:
      // - https://www.npmjs.com/package/worker-plugin
      // - https://developer.mozilla.org/en-US/docs/Web/API/Worker/Worker#browser_compatibility
      worker = new Worker('./WorkerMergeDashboardTableRowsByDimension.js', { type: 'module' });
      workerList[index] = worker;
    }

    return worker;
  }

  /**
   * Async function that uses a pool of Web Worker to process mergeDashboardTableRowsByDimension()
   * @param {object} p
   * @param {DashboardTableRow[]} p.rows
   * @param {string} p.dimensionId
   * @param {string} [p.childrenDimensionId]
   * @param {function} [p.additionalMerger]
   * @param {boolean} [p.isCustomTable]
   * @param {DashboardFilter} [p.dashboardFilter]
   * @param {*} [p.currency]
   * @param {Object[]} [p.customMetrics]
   */
  workerMergeDashboardTableRowsByDimension({
    rows,
    dimensionId,
    childrenDimensionId,
    dashboardFilter,
    currency,
    customMetrics,
    isCustomTable,
    additionalMerger,
  }) {
    if (!this.useWorker) {
      return mergeDashboardTableRowsByDimension({
        rows,
        dimensionId,
        childrenDimensionId,
        dashboardFilter,
        currency,
        customMetrics,
        isCustomTable,
        additionalMerger,
      });
    }

    workerIndex = (workerIndex + 1) % workerCount;
    const inUseWorkerIndex = workerIndex;
    const worker = this.getOrInitWorkerFromPool(inUseWorkerIndex);
    const uuid = v4();

    return new Promise((resolve, reject) => {
      const callback = e => {
        if (e.data.key === uuid) {
          worker.removeEventListener('message', callback);
          resolve(e.data.result);
        }
      };

      worker.postMessage({
        rows,
        dimensionId,
        childrenDimensionId,
        dashboardFilter,
        currency,
        customMetrics,
        isCustomTable,
        additionalMerger,
        key: uuid,
      });
      worker.addEventListener('message', callback);
      worker.onerror = err => {
        worker.terminate();
        workerList[inUseWorkerIndex] = undefined;
        reject(err);
      };
    });
  }
}
module.exports = {
  DashboardClient,
  workerList,
  workerCount,
};

let promisesByWidgetId = {};

function tablePromise2callBack({ widgetId, promises, reRenderCallBack, failCallBack, doneCallBack, debug = false }) {
  if (widgetId) {
    // Convert to bluebird promises.
    promises = promises.map(Bluebird.resolve);
    // Cancel the promises previously made for the same widget.
    let previousPromises = promisesByWidgetId[widgetId];
    if (previousPromises) {
      for (let promise of previousPromises) promise.cancel();
    }
    promisesByWidgetId[widgetId] = promises;
  }
  // Call all promises in parallel and call the callbacks when they are done.
  const throttledReRenderCallBack = reRenderCallBack ? _.throttle(reRenderCallBack, 500, { leading: true }) : null;
  let $table;
  Bluebird.map(promises, ({ table, isSuccess, adAccount, adAccountTable, error }) => {
    if (!isSuccess) {
      failCallBack({ adAccount, error });
    } else {
      if (throttledReRenderCallBack) throttledReRenderCallBack({ table, adAccountTable, adAccount });
      $table = table;
    }
  })
    .catch(error => {
      failCallBack({ error });
    })
    .then(() => {
      if (throttledReRenderCallBack) {
        throttledReRenderCallBack.cancel();
        reRenderCallBack({ table: $table });
      }
      if (debug) {
        console.log('doneCallBack', { table: $table });
      }
      if (doneCallBack) doneCallBack({ table: $table });
    });
  return promises;
}

function buildArchitectureFilter({
  channel,
  level,
  channelAdAccountId,
  channelCampaignId,
  channelAdSetId,
  channelAdId,
  channelKeywordId,
}) {
  if (!channel || !level) {
    throw new Error('Bad arguments.');
  }
  if (channel.includes('/')) {
    channel = channel.split('/')[0];
  }
  let architectureFilter = { adAccount: { channel, channelId: channelAdAccountId } };
  if (['campaign', 'adSet', 'ad', 'keyword'].includes(level)) {
    if (!channelCampaignId) throw new Error('Bad arguments.');
    architectureFilter['campaigns'] = [{ channelId: channelCampaignId }];
  }
  if (['adSet', 'ad', 'keyword'].includes(level)) {
    if (!channelAdSetId) throw new Error('Bad arguments.');
    architectureFilter['adSets'] = [{ channelId: channelAdSetId }];
  }
  if (['ad'].includes(level)) {
    if (!channelAdId) throw new Error('Bad arguments.');
    architectureFilter['ads'] = [{ channelId: channelAdId }];
  }
  if (['keyword'].includes(level)) {
    if (!channelKeywordId) throw new Error('Bad arguments.');
    architectureFilter['keywords'] = [{ channelId: channelKeywordId }];
  }
  return architectureFilter;
}

// Walk through the entire table.
// Apply the callback cb to each row including children rows.
function walkTable(table, cb) {
  if (!Array.isArray(table)) return walkTable([table], cb);
  for (let row of table) {
    cb(row);
    if (row.children) walkTable(row.children, cb);
  }
}

function isInUpdatePeriod(lastUpdatedAt, dateFilter) {
  if (!lastUpdatedAt) return true;
  lastUpdatedAt = moment(lastUpdatedAt);
  // Verify if we need to request latest data(by refreshing cache)
  const today = moment().startOf('day'); // Moment, yyyy-MM-dd 00:00:00
  const toDate = moment(dateFilter.to).startOf('day'); // Moment yyyy-MM-dd 00:00:00
  const now = moment();
  // If 1) 'dateFilter.to' > (today - 3 days) // report(s) on a specific date can be updated upto 3 days after the dates
  // And 2) AND difference between now and last updated date is same or more than 1 hour,
  // then we need to get refreshed data and the server will also refresh its cache
  const isInUpdatePeriod = today.diff(toDate, 'hours') - 24 < 72;
  return isInUpdatePeriod && now.diff(lastUpdatedAt, 'hours') >= 1;
}
